import { useState, useEffect } from "react";
import { FolderOpen, FileText, Trash2, Eye, Clock, Plane, Loader2, Search, Plus, Download, FileCode, CheckSquare, Square, Skull, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import html2pdf from "html2pdf.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aircraft type name mapping
const AIRCRAFT_TYPE_NAMES = {
  da42mpp: "Diamond DA42 MPP",
  da62mpp: "Diamond DA62 MPP",
};

const getAircraftTypeName = (type) => {
  return AIRCRAFT_TYPE_NAMES[type] || type || '-';
};

// Route airports mapping by FTX designator
const FTX_ROUTE_AIRPORTS = {
  "SPARROW 4": ["EBOS", "LFAC", "LFAT"],
  "sparrow4": ["EBOS", "LFAC", "LFAT"],
};

// Get route string for FTX designator
const getRouteString = (ftxDesignator, fallbackIcao) => {
  const route = FTX_ROUTE_AIRPORTS[ftxDesignator];
  if (route && route.length > 0) {
    return route.join(' → ');
  }
  return fallbackIcao || '';
};

export const BriefingLibrary = ({ onLoadBriefing, onNewBriefing, onDownloadBriefing }) => {
  const [briefings, setBriefings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [briefingToDelete, setBriefingToDelete] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewBriefing, setViewBriefing] = useState(null);
  const [selectedBriefings, setSelectedBriefings] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  
  // Lock report state
  const [lockPassword, setLockPassword] = useState("");
  const [briefingToLock, setBriefingToLock] = useState(null);
  const [lockingInProgress, setLockingInProgress] = useState(false);

  useEffect(() => {
    fetchBriefings();
  }, []);

  const fetchBriefings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/briefings`);
      setBriefings(response.data);
    } catch (error) {
      console.error("Failed to fetch briefings:", error);
      toast.error("Failed to load briefings");
    } finally {
      setLoading(false);
    }
  };

  // Handle lock report (XDU only)
  const handleLockReport = async (briefing) => {
    if (lockPassword !== "diamond") {
      toast.error("Invalid password. Only XDU 🥳 can lock reports.");
      return;
    }
    
    setLockingInProgress(true);
    try {
      await axios.patch(`${API}/briefings/${briefing.id}`, { 
        isLocked: true,
        lockedAt: new Date().toISOString(),
        lockedBy: "XDU 🥳"
      });
      
      // Update local state
      setBriefings(briefings.map(b => 
        b.id === briefing.id 
          ? { ...b, isLocked: true, lockedAt: new Date().toISOString(), lockedBy: "XDU 🥳" } 
          : b
      ));
      
      toast.success("Report locked by XDU 🥳");
      setBriefingToLock(null);
      setLockPassword("");
    } catch (error) {
      console.error("Failed to lock briefing:", error);
      toast.error("Failed to lock report");
    } finally {
      setLockingInProgress(false);
    }
  };

  const handleDelete = async () => {
    if (!briefingToDelete) return;
    
    try {
      await axios.delete(`${API}/briefings/${briefingToDelete.id}`);
      setBriefings(briefings.filter(b => b.id !== briefingToDelete.id));
      setSelectedBriefings(selectedBriefings.filter(id => id !== briefingToDelete.id));
      toast.success("Briefing deleted");
    } catch (error) {
      toast.error("Failed to delete briefing");
    } finally {
      setDeleteDialogOpen(false);
      setBriefingToDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedBriefings.map(id => axios.delete(`${API}/briefings/${id}`))
      );
      setBriefings(briefings.filter(b => !selectedBriefings.includes(b.id)));
      toast.success(`${selectedBriefings.length} briefing(s) deleted`);
      setSelectedBriefings([]);
    } catch (error) {
      toast.error("Failed to delete some briefings");
    } finally {
      setBulkDeleteDialogOpen(false);
    }
  };

  const toggleSelection = (briefingId) => {
    setSelectedBriefings(prev => 
      prev.includes(briefingId) 
        ? prev.filter(id => id !== briefingId)
        : [...prev, briefingId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBriefings.length === filteredBriefings.length) {
      setSelectedBriefings([]);
    } else {
      setSelectedBriefings(filteredBriefings.map(b => b.id));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrentDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const generatePDF = async (briefing) => {
    // If callback provided, use it to generate full briefing PDF
    if (onDownloadBriefing) {
      setDownloading(briefing.id);
      try {
        await onDownloadBriefing(briefing, 'pdf');
      } finally {
        setDownloading(null);
      }
      return;
    }
    
    // Fallback to simple PDF generation
    setDownloading(briefing.id);
    try {
      const formData = briefing.formData || {};
      const icao = briefing.icao || '';
      
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto;">
          <h1 style="text-align: center; color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px;">
            Weather Briefing - ${briefing.title}
          </h1>
          <p style="text-align: center; color: #64748b; font-size: 12px;">
            Generated: ${getCurrentDate()} | ICAO: ${icao}
          </p>
          
          <div style="margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px;">
            <h3 style="color: #334155; margin-bottom: 10px;">Flight Information</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <tr>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold; width: 25%;">FTX Designator</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${formData.ftxDesignator || '-'}</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold; width: 25%;">Registration</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${formData.registration || '-'}</td>
              </tr>
              <tr>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold;">Aircraft Type</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${getAircraftTypeName(formData.aircraftType)}</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold;">Callsign</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${formData.callsign || '-'}</td>
              </tr>
            </table>
          </div>

          <div style="margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px;">
            <h3 style="color: #334155; margin-bottom: 10px;">Crew</h3>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <tr>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold; width: 25%;">PIC</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${formData.crewPIC || '-'}</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold; width: 25%;">PM</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${formData.crewPM || '-'}</td>
              </tr>
              <tr>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold;">Sensor Operator</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${formData.crewSIC || '-'}</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0; background: #f1f5f9; font-weight: bold;">Liaison Officer</td>
                <td style="padding: 8px; border: 1px solid #e2e8f0;">${formData.crewLiaisonOfficer || '-'}</td>
              </tr>
            </table>
          </div>

          ${formData.missionWeather ? `
            <div style="margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px;">
              <h3 style="color: #334155; margin-bottom: 10px;">Mission Weather Notes</h3>
              <p style="font-size: 12px; white-space: pre-wrap;">${formData.missionWeather}</p>
            </div>
          ` : ''}

          ${formData.crewRecommendations ? `
            <div style="margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px;">
              <h3 style="color: #334155; margin-bottom: 10px;">Crew Recommendations</h3>
              <p style="font-size: 12px; white-space: pre-wrap;">${formData.crewRecommendations}</p>
            </div>
          ` : ''}

          <div style="margin-top: 20px; padding: 15px; background: #f8fafc; border-radius: 8px;">
            <h3 style="color: #334155; margin-bottom: 10px;">Flight Limitations</h3>
            <div style="display: flex; flex-wrap: wrap; gap: 8px;">
              ${formData.poorVisibility ? '<span style="padding: 4px 8px; background: #fef3c7; color: #92400e; border-radius: 4px; font-size: 11px;">Poor Visibility</span>' : ''}
              ${formData.lowClouds ? '<span style="padding: 4px 8px; background: #fef3c7; color: #92400e; border-radius: 4px; font-size: 11px;">Low Clouds</span>' : ''}
              ${formData.crosswind ? '<span style="padding: 4px 8px; background: #fef3c7; color: #92400e; border-radius: 4px; font-size: 11px;">Crosswind > 20 KTS</span>' : ''}
              ${formData.thunderstorm ? '<span style="padding: 4px 8px; background: #fee2e2; color: #991b1b; border-radius: 4px; font-size: 11px;">Thunderstorm / CB</span>' : ''}
              ${formData.icingConditions ? '<span style="padding: 4px 8px; background: #dbeafe; color: #1e40af; border-radius: 4px; font-size: 11px;">Known Icing</span>' : ''}
              ${!formData.poorVisibility && !formData.lowClouds && !formData.crosswind && !formData.thunderstorm && !formData.icingConditions ? '<span style="color: #64748b; font-size: 12px;">No limitations flagged</span>' : ''}
            </div>
          </div>
        </div>
      `;

      const element = document.createElement('div');
      element.innerHTML = htmlContent;
      document.body.appendChild(element);

      const opt = {
        margin: 10,
        filename: `Wx_${formData.ftxDesignator || 'Briefing'}_${icao}_${getCurrentDate().replace(/\//g, '')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(element).save();
      document.body.removeChild(element);
      toast.success("PDF downloaded");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(null);
    }
  };

  const generateHTML = (briefing) => {
    // If callback provided, use it to generate full briefing HTML
    if (onDownloadBriefing) {
      setDownloading(briefing.id);
      onDownloadBriefing(briefing, 'html').finally(() => {
        setDownloading(null);
      });
      return;
    }
    
    // Fallback to simple HTML generation
    setDownloading(briefing.id);
    try {
      const formData = briefing.formData || {};
      const icao = briefing.icao || '';
      
      const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Weather Briefing - ${briefing.title}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; background: #f8fafc; }
    h1 { text-align: center; color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
    .meta { text-align: center; color: #64748b; font-size: 12px; margin-bottom: 20px; }
    .section { margin-top: 20px; padding: 15px; background: white; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    h3 { color: #334155; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    td { padding: 8px; border: 1px solid #e2e8f0; }
    .label { background: #f1f5f9; font-weight: bold; width: 25%; }
    .badge { padding: 4px 8px; border-radius: 4px; font-size: 11px; display: inline-block; margin: 2px; }
    .badge-amber { background: #fef3c7; color: #92400e; }
    .badge-red { background: #fee2e2; color: #991b1b; }
    .badge-blue { background: #dbeafe; color: #1e40af; }
    pre { white-space: pre-wrap; font-size: 12px; margin: 0; font-family: inherit; }
  </style>
</head>
<body>
  <h1>Weather Briefing - ${briefing.title}</h1>
  <p class="meta">Generated: ${getCurrentDate()} | ICAO: ${icao}</p>
  
  <div class="section">
    <h3>Flight Information</h3>
    <table>
      <tr>
        <td class="label">FTX Designator</td>
        <td>${formData.ftxDesignator || '-'}</td>
        <td class="label">Registration</td>
        <td>${formData.registration || '-'}</td>
      </tr>
      <tr>
        <td class="label">Aircraft Type</td>
        <td>${getAircraftTypeName(formData.aircraftType)}</td>
        <td class="label">Callsign</td>
        <td>${formData.callsign || '-'}</td>
      </tr>
    </table>
  </div>

  <div class="section">
    <h3>Crew</h3>
    <table>
      <tr>
        <td class="label">PIC</td>
        <td>${formData.crewPIC || '-'}</td>
        <td class="label">PM</td>
        <td>${formData.crewPM || '-'}</td>
      </tr>
      <tr>
        <td class="label">Sensor Operator</td>
        <td>${formData.crewSIC || '-'}</td>
        <td class="label">Liaison Officer</td>
        <td>${formData.crewLiaisonOfficer || '-'}</td>
      </tr>
    </table>
  </div>

  ${formData.missionWeather ? `
    <div class="section">
      <h3>Mission Weather Notes</h3>
      <pre>${formData.missionWeather}</pre>
    </div>
  ` : ''}

  ${formData.crewRecommendations ? `
    <div class="section">
      <h3>Crew Recommendations</h3>
      <pre>${formData.crewRecommendations}</pre>
    </div>
  ` : ''}

  <div class="section">
    <h3>Flight Limitations</h3>
    ${formData.poorVisibility ? '<span class="badge badge-amber">Poor Visibility</span>' : ''}
    ${formData.lowClouds ? '<span class="badge badge-amber">Low Clouds</span>' : ''}
    ${formData.crosswind ? '<span class="badge badge-amber">Crosswind > 20 KTS</span>' : ''}
    ${formData.thunderstorm ? '<span class="badge badge-red">Thunderstorm / CB</span>' : ''}
    ${formData.icingConditions ? '<span class="badge badge-blue">Known Icing</span>' : ''}
    ${!formData.poorVisibility && !formData.lowClouds && !formData.crosswind && !formData.thunderstorm && !formData.icingConditions ? '<span style="color: #64748b; font-size: 12px;">No limitations flagged</span>' : ''}
  </div>
</body>
</html>`;

      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Wx_${formData.ftxDesignator || 'Briefing'}_${icao}_${getCurrentDate().replace(/\//g, '')}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("HTML downloaded");
    } catch (error) {
      console.error("HTML generation error:", error);
      toast.error("Failed to generate HTML");
    } finally {
      setDownloading(null);
    }
  };

  const filteredBriefings = briefings.filter(b => {
    const title = b.title || b.pilotTrigram || '';
    const icao = b.icao || (b.airports && b.airports.join(' ')) || '';
    const searchLower = searchQuery.toLowerCase();
    return title.toLowerCase().includes(searchLower) ||
           icao.toLowerCase().includes(searchLower);
  });

  const getFlightCategoryStyle = (category) => {
    switch (category) {
      case 'VFR': return 'bg-emerald-100 text-emerald-700';
      case 'MVFR': return 'bg-blue-100 text-blue-700';
      case 'IFR': return 'bg-red-100 text-red-700';
      case 'LIFR': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="weather-card p-6 min-h-[calc(100vh-220px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-100 border border-indigo-200">
            <FolderOpen className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">Briefing Library</h3>
            <p className="text-xs text-slate-500">
              {briefings.length} saved briefing{briefings.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedBriefings.length > 0 && (
            <Button
              variant="destructive"
              onClick={() => setBulkDeleteDialogOpen(true)}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedBriefings.length})
            </Button>
          )}
          <Button
            onClick={onNewBriefing}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Briefing
          </Button>
        </div>
      </div>

      {/* Search and Select All */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search briefings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-slate-300"
          />
        </div>
        {filteredBriefings.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={toggleSelectAll}
            className="text-slate-600"
          >
            {selectedBriefings.length === filteredBriefings.length ? (
              <>
                <CheckSquare className="w-4 h-4 mr-2" />
                Deselect All
              </>
            ) : (
              <>
                <Square className="w-4 h-4 mr-2" />
                Select All
              </>
            )}
          </Button>
        )}
      </div>

      {/* Briefings List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 flex-1">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mr-2" />
          <span className="text-slate-600">Loading briefings...</span>
        </div>
      ) : filteredBriefings.length === 0 ? (
        <div className="text-center py-12 flex-1">
          <div className="inline-flex p-4 rounded-full bg-slate-100 mb-4">
            <FileText className="w-8 h-8 text-slate-400" />
          </div>
          <h4 className="text-lg font-medium text-slate-700 mb-1">
            {searchQuery ? "No matching briefings" : "No saved briefings"}
          </h4>
          <p className="text-sm text-slate-500">
            {searchQuery ? "Try a different search term" : "Create a new briefing to get started"}
          </p>
        </div>
      ) : (
        <ScrollArea className="flex-1 min-h-0">
          <div className="space-y-3 pr-4">
            {filteredBriefings.map((briefing) => (
              <div
                key={briefing.id}
                className={`group p-4 rounded-lg border bg-white hover:shadow-sm transition-all ${
                  selectedBriefings.includes(briefing.id) 
                    ? 'border-indigo-400 bg-indigo-50' 
                    : 'border-slate-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <Checkbox
                    checked={selectedBriefings.includes(briefing.id)}
                    onCheckedChange={() => toggleSelection(briefing.id)}
                    className="mt-1"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-slate-800 truncate">
                        {briefing.title || (briefing.source === 'weather_chat' 
                          ? `Weather Chat Draft - ${briefing.pilotTrigram || 'Unknown'}` 
                          : 'Untitled Briefing')}
                      </h4>
                      {briefing.source === 'weather_chat' && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                          Chat Draft
                        </span>
                      )}
                      {briefing.flightCategory && (
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${getFlightCategoryStyle(briefing.flightCategory)}`}>
                          {briefing.flightCategory}
                        </span>
                      )}
                      {/* Lock icon - shows locked state or skull for locking */}
                      {briefing.isLocked ? (
                        <span className="text-slate-400" title={`Locked by ${briefing.lockedBy || 'XDU 🥳'}`}>
                          <Lock className="w-4 h-4" />
                        </span>
                      ) : (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button 
                              className="text-slate-300 hover:text-slate-600 transition-colors opacity-0 group-hover:opacity-100"
                              title="Lock report"
                            >
                              <Skull className="w-4 h-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-3" align="start">
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                <Lock className="w-4 h-4" />
                                Lock Report
                              </div>
                              <p className="text-xs text-slate-500">
                                Only XDU 🥳 can lock reports. Enter password to confirm.
                              </p>
                              <Input
                                type="password"
                                placeholder="Enter XDU 🥳 password"
                                value={briefingToLock?.id === briefing.id ? lockPassword : ""}
                                onChange={(e) => {
                                  setBriefingToLock(briefing);
                                  setLockPassword(e.target.value);
                                }}
                                className="text-sm"
                              />
                              <Button
                                size="sm"
                                className="w-full bg-slate-800 hover:bg-slate-900"
                                onClick={() => handleLockReport(briefing)}
                                disabled={lockingInProgress || !lockPassword}
                              >
                                {lockingInProgress ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <Lock className="w-4 h-4 mr-2" />
                                )}
                                Lock Report
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    {/* Route airports - handle chat drafts */}
                    {briefing.source === 'weather_chat' && briefing.airports?.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-purple-600 font-medium mb-1">
                        <Plane className="w-3 h-3" />
                        {briefing.airports.join(' → ')}
                      </div>
                    )}
                    {briefing.formData?.ftxDesignator && FTX_ROUTE_AIRPORTS[briefing.formData.ftxDesignator] && (
                      <div className="flex items-center gap-1 text-xs text-indigo-600 font-medium mb-1">
                        <Plane className="w-3 h-3" />
                        {getRouteString(briefing.formData.ftxDesignator, briefing.icao)}
                      </div>
                    )}
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {briefing.source !== 'weather_chat' && (!briefing.formData?.ftxDesignator || !FTX_ROUTE_AIRPORTS[briefing.formData.ftxDesignator]) && (
                        <span className="flex items-center gap-1">
                          <Plane className="w-3 h-3" />
                          {briefing.icao}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(briefing.created_at)}
                      </span>
                      {briefing.pilotTrigram && (
                        <span className="text-purple-600 font-medium">
                          by {briefing.pilotTrigram}
                        </span>
                      )}
                    </div>
                    {(briefing.formData?.crewPIC || briefing.formData?.crewPM || briefing.formData?.crewSIC || briefing.formData?.crewLiaisonOfficer) && (
                      <p className="text-xs text-slate-500 mt-1">
                        {[
                          briefing.formData?.crewPIC && `PIC: ${briefing.formData.crewPIC}`,
                          briefing.formData?.crewPM && `PM: ${briefing.formData.crewPM}`,
                          briefing.formData?.crewSIC && `SO: ${briefing.formData.crewSIC}`,
                          briefing.formData?.crewLiaisonOfficer && `LO: ${briefing.formData.crewLiaisonOfficer}${briefing.formData.crewLiaisonOfficer.includes('XDU') && !briefing.formData.crewLiaisonOfficer.includes('🥳') ? ' 🥳' : ''}`
                        ].filter(Boolean).join(' • ')}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    {/* Download Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-500 hover:text-emerald-600"
                          disabled={downloading === briefing.id}
                        >
                          {downloading === briefing.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => generatePDF(briefing)} className="cursor-pointer">
                          <FileText className="w-4 h-4 mr-2" />
                          Download PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => generateHTML(briefing)} className="cursor-pointer">
                          <FileCode className="w-4 h-4 mr-2" />
                          Download HTML
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setViewBriefing(briefing);
                        setViewDialogOpen(true);
                      }}
                      className="text-slate-500 hover:text-indigo-600"
                      title="Quick view"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onLoadBriefing(briefing)}
                      className="text-slate-500 hover:text-emerald-600"
                      title="Detailed view"
                    >
                      <Search className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setBriefingToDelete(briefing);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-slate-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              {viewBriefing?.title}
            </DialogTitle>
            <DialogDescription>
              {viewBriefing?.formData?.ftxDesignator && FTX_ROUTE_AIRPORTS[viewBriefing.formData.ftxDesignator]
                ? getRouteString(viewBriefing.formData.ftxDesignator, viewBriefing.icao)
                : viewBriefing?.icao || 'Unknown'
              } • Created {viewBriefing && formatDate(viewBriefing.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {viewBriefing && (
            <div className="space-y-4 py-4">
              {/* Flight Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 uppercase">FTX Designator</p>
                  <p className="font-medium">{viewBriefing.formData?.ftxDesignator || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Registration</p>
                  <p className="font-medium">{viewBriefing.formData?.registration || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Aircraft Type</p>
                  <p className="font-medium">{getAircraftTypeName(viewBriefing.formData?.aircraftType)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Callsign</p>
                  <p className="font-medium">{viewBriefing.formData?.callsign || '-'}</p>
                </div>
              </div>

              {/* Crew */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 uppercase">PIC</p>
                  <p className="font-medium">{viewBriefing.formData?.crewPIC || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">PM</p>
                  <p className="font-medium">{viewBriefing.formData?.crewPM || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Sensor Operator</p>
                  <p className="font-medium">{viewBriefing.formData?.crewSIC || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Liaison Officer</p>
                  <p className="font-medium">{viewBriefing.formData?.crewLiaisonOfficer || '-'}</p>
                </div>
              </div>

              {/* Weather Notes */}
              {viewBriefing.formData?.missionWeather && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase mb-2">Mission Weather Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{viewBriefing.formData.missionWeather}</p>
                </div>
              )}

              {/* Crew Recommendations */}
              {viewBriefing.formData?.crewRecommendations && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase mb-2">Crew Recommendations</p>
                  <p className="text-sm whitespace-pre-wrap">{viewBriefing.formData.crewRecommendations}</p>
                </div>
              )}

              {/* Flight Limitations */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase mb-2">Flight Limitations</p>
                <div className="flex flex-wrap gap-2">
                  {viewBriefing.formData?.poorVisibility && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">Poor Visibility</span>
                  )}
                  {viewBriefing.formData?.lowClouds && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">Low Clouds</span>
                  )}
                  {viewBriefing.formData?.crosswind && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">Crosswind &gt; 20 KTS</span>
                  )}
                  {viewBriefing.formData?.thunderstorm && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Thunderstorm / CB</span>
                  )}
                  {viewBriefing.formData?.icingConditions && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Known Icing</span>
                  )}
                  {!viewBriefing.formData?.poorVisibility && 
                   !viewBriefing.formData?.lowClouds && 
                   !viewBriefing.formData?.crosswind && 
                   !viewBriefing.formData?.thunderstorm && 
                   !viewBriefing.formData?.icingConditions && (
                    <span className="text-sm text-slate-500">No limitations flagged</span>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => generatePDF(viewBriefing)}
                disabled={downloading === viewBriefing?.id}
              >
                {downloading === viewBriefing?.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                PDF
              </Button>
              <Button 
                variant="outline" 
                onClick={() => generateHTML(viewBriefing)}
                disabled={downloading === viewBriefing?.id}
              >
                <FileCode className="w-4 h-4 mr-2" />
                HTML
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
              <Button 
                onClick={() => {
                  onLoadBriefing(viewBriefing);
                  setViewDialogOpen(false);
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Search className="w-4 h-4 mr-2" />
                Detailed View
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Briefing?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{briefingToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedBriefings.length} Briefing(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedBriefings.length} selected briefing(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BriefingLibrary;
